import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ResponseDriverDemand, ResponseDriverDemandTable, ResponseJourneyDemand, ResponseJourneyDemandTable, ResponseToken } from '@models/responses/stats-response';
import { lastValueFrom, Observable } from 'rxjs';
import { MethodsGeneralService } from '@providers/methods-general/methods-general/methods-general';
import { ParamsDriverDemand, ParamsDriverDemandTable, ParamsJourneyDemand, ParamsJourneyDemandTable } from '@models/entities/stats/stats';
import { SessionDataProvider } from '@providers/session-data/session-data';
import { StorageService } from '@providers/storage/storage.service';
import { StorageKeys } from '@enums/storage.enum';
import { SnackbarService } from '@providers/snackbar-services/snackbar.services';

@Injectable({
  providedIn: 'root',
})
export class StatsService {
  private _url: string;
  private readonly token = '/user/token';
  private readonly journeyDemand = '/journey/demand';
  private readonly journeyDemandTable = '/journey/comparison/table';
  private readonly driverDemand = '/driver/demand';
  private readonly driverDemandTable = '/driver/comparison/table';

  constructor(
    private http: HttpClient,
    private methodGeneral: MethodsGeneralService,
    private sessionData: SessionDataProvider,
    private snackbar: SnackbarService,
    private storage: StorageService
  ) {
    this._url = environment.serverStats;
  }

  async getTokenStat(): Promise<ResponseToken> {
    const login$ = this.http.get<ResponseToken>(
      `${this._url}${this.token}`
    );

    return await lastValueFrom(login$);
  }

  getTokenStatObservable(): Observable<ResponseToken> {
    return this.http.get<ResponseToken>(
      `${this._url}${this.token}`
    );
  }

  async getJourneyDemand(params: ParamsJourneyDemand): Promise<ResponseJourneyDemand> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.sessionData.tokenStats}`
    });
    const demand$ = this.http.get<ResponseJourneyDemand>(
      `${this._url}${this.journeyDemand}?${this.methodGeneral.toQueryString(params, false)}`,
      { headers }
    );

    return await lastValueFrom(demand$);
  }

  async getJourneyDemandTable(params: ParamsJourneyDemandTable): Promise<ResponseJourneyDemandTable> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.sessionData.tokenStats}`
    });
    const demandTable$ = this.http.get<ResponseJourneyDemandTable>(
      `${this._url}${this.journeyDemandTable}?${this.methodGeneral.toQueryString(params, false)}`,
      { headers }
    );

    return await lastValueFrom(demandTable$);
  }

  async getDriverDemand(params: ParamsDriverDemand): Promise<ResponseDriverDemand> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.sessionData.tokenStats}`
    });
    const driverDemand$ = this.http.get<ResponseDriverDemand>(
      `${this._url}${this.driverDemand}?${this.methodGeneral.toQueryString(params, false)}`,
      { headers }
    );

    return await lastValueFrom(driverDemand$);
  }

  async getDriverDemandTable(params: ParamsDriverDemandTable): Promise<ResponseDriverDemandTable> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${this.sessionData.tokenStats}`
    });
    const driverTable$ = this.http.get<ResponseDriverDemandTable>(
      `${this._url}${this.driverDemandTable}?${this.methodGeneral.toQueryString(params, false)}`,
      { headers }
    );

    return await lastValueFrom(driverTable$);
  }

  async loginStats(): Promise<void> {
    try {
      const response: ResponseToken = await this.getTokenStat();
      const { success, data, message, description } = response;
      if (success) {
        this.sessionData.tokenStats = data?.accessToken ?? '';
        await this.storage.setData(StorageKeys.tokenStats, this.sessionData.tokenStats);
      } else {
        this.snackbar.showSnackBar(message, description);
      }
    } catch(error) {
      if (error?.message && error?.title) {
        this.snackbar.showSnackBar(error.title, error.message);
      }
    }
  }
}
